/**
 * Track selection for auto-mix (X5-X10).
 *
 * Pure logic over plain data: no audio, no DOM, no fetch. Selection
 * distribution is not something anyone can verify by listening — a subtly wrong
 * weighting just sounds like an unlucky shuffle — so this is kept separable and
 * unit tested instead.
 */

export interface Candidate {
  id: number;
  /** Effective BPM, or null where no tempo could be asserted (A9). */
  tempo: number | null;
}

export interface SelectorConfig {
  /**
   * 0..1. Low keeps selection inside a tempo band, so beat-aligned transitions
   * happen often; high ranges freely across the library.
   */
  temperature: number;
  /** Tempo fader travel, as a fraction — how far a tempo can be pulled. */
  tempoRange: number;
}

export const DEFAULT_SELECTOR_CONFIG: SelectorConfig = {
  temperature: 0.35,
  tempoRange: 0.16,
};

/** Score given to a track that has no tempo to compare (X9). */
const NO_TEMPO_SCORE = 0.45;

/** How much of the score is tempo compatibility, versus how long ago it played. */
const TEMPO_WEIGHT = 0.7;
const RECENCY_WEIGHT = 0.3;

/** Softmax temperature bounds. Low is decisive, high is nearly uniform. */
const MIN_T = 0.05;
const MAX_T = 1.0;

/**
 * How closely `candidate` can be pulled to `reference`, as 0..1.
 *
 * Half and double time are considered, because a 174 BPM track sits perfectly
 * against an 87 BPM one and refusing that would discard most of the useful
 * pairings in any library that spans genres.
 */
export function tempoScore(
  candidate: number | null,
  reference: number | null,
  tempoRange: number,
): number {
  if (candidate === null || reference === null || candidate <= 0 || reference <= 0) {
    return NO_TEMPO_SCORE;
  }

  let best = Infinity;
  for (const octave of [0.5, 1, 2]) {
    best = Math.min(best, Math.abs((candidate * octave) / reference - 1));
  }

  if (best <= tempoRange) return 1;
  // Beyond reach, decay rather than cut off: a slightly-too-far track is still
  // a better neighbour than one at half the speed.
  return Math.max(0, 1 - (best - tempoRange) / (tempoRange * 2));
}

export interface SelectorState {
  poolSize: number;
  queued: number[];
  recentlyPlayed: number[];
}

export class Selector {
  private library: Candidate[] = [];
  private unplayed = new Set<number>();
  private queue: number[] = [];
  private playedAt = new Map<number, number>();
  private clock = 0;

  constructor(private readonly random: () => number = Math.random) {}

  setLibrary(candidates: Candidate[]): void {
    this.library = candidates;
    const ids = new Set(candidates.map((c) => c.id));
    for (const id of [...this.playedAt.keys()]) {
      if (!ids.has(id)) this.playedAt.delete(id);
    }
    this.queue = this.queue.filter((id) => ids.has(id));
    this.refillIfEmpty(true);
  }

  private refillIfEmpty(force = false): void {
    if (!force && this.unplayed.size > 0) return;
    this.unplayed = new Set(this.library.map((c) => c.id));
  }

  // -- manual queue (X5) ------------------------------------------------

  enqueue(id: number): void {
    if (!this.queue.includes(id)) this.queue.push(id);
  }

  dequeue(id: number): void {
    this.queue = this.queue.filter((queued) => queued !== id);
  }

  clearQueue(): void {
    this.queue = [];
  }

  state(): SelectorState {
    const recent = [...this.playedAt.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([id]) => id);
    return { poolSize: this.unplayed.size, queued: [...this.queue], recentlyPlayed: recent };
  }

  markPlayed(id: number): void {
    this.playedAt.set(id, ++this.clock);
    this.unplayed.delete(id);
    // Refill only once genuinely exhausted, which is what guarantees every
    // track plays before any track repeats (X7).
    this.refillIfEmpty();
  }

  /** How long ago this played, as 0 (just now) to 1 (never, or long ago). */
  private recencyScore(id: number): number {
    const at = this.playedAt.get(id);
    if (at === undefined) return 1;
    const age = this.clock - at;
    const span = Math.max(1, this.library.length);
    return Math.min(1, age / span);
  }

  /**
   * Choose the next track.
   *
   * `ignoreTempo` is the "mix it up" path (X8a): a uniform pick from the pool,
   * bypassing scoring entirely.
   */
  next(
    reference: number | null,
    config: SelectorConfig,
    options: { ignoreTempo?: boolean; exclude?: number[] } = {},
  ): Candidate | null {
    const excluded = new Set(options.exclude ?? []);

    while (this.queue.length > 0) {
      const id = this.queue.shift() as number;
      const track = this.library.find((c) => c.id === id);
      if (track && !excluded.has(id)) return track;
    }

    let pool = this.library.filter((c) => this.unplayed.has(c.id) && !excluded.has(c.id));
    if (pool.length === 0) {
      // Exhausted, or everything left is excluded: fall back to the whole
      // library so auto-mix never runs out of things to play (X3).
      pool = this.library.filter((c) => !excluded.has(c.id));
    }
    if (pool.length === 0) return null;

    if (options.ignoreTempo) {
      return pool[Math.floor(this.random() * pool.length)] ?? null;
    }

    const temperature = Math.max(0, Math.min(1, config.temperature));
    const t = MIN_T + temperature * (MAX_T - MIN_T);

    const weights = pool.map((candidate) => {
      const score =
        TEMPO_WEIGHT * tempoScore(candidate.tempo, reference, config.tempoRange) +
        RECENCY_WEIGHT * this.recencyScore(candidate.id);
      return Math.exp(score / t);
    });

    const total = weights.reduce((sum, w) => sum + w, 0);
    if (!Number.isFinite(total) || total <= 0) {
      return pool[Math.floor(this.random() * pool.length)] ?? null;
    }

    let target = this.random() * total;
    for (let i = 0; i < pool.length; i++) {
      target -= weights[i] as number;
      if (target <= 0) return pool[i] as Candidate;
    }
    return pool[pool.length - 1] as Candidate;
  }
}
